import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ProductForm = ({ product, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    productName: '',
    category: 'zonnepanelen',
    description: '',
    priceRange: '',
    imageUrl: '',
    status: 'draft',
    seoTitle: '',
    seoDescription: '',
    technicalSpecs: {
      spec1: '',
      spec2: '',
      spec3: '',
      spec4: '',
    },
  });

  useEffect(() => {
    if (product) {
      setFormData({
        productName: product?.product_name || '',
        category: product?.category || 'zonnepanelen',
        description: product?.description || '',
        priceRange: product?.price_range || '',
        imageUrl: product?.image_url || '',
        status: product?.status || 'draft',
        seoTitle: product?.seo_title || '',
        seoDescription: product?.seo_description || '',
        technicalSpecs: product?.technical_specs || {
          spec1: '',
          spec2: '',
          spec3: '',
          spec4: '',
        },
      });
    }
  }, [product]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);

    try {
      const productData = {
        provider_id: user?.id,
        product_name: formData?.productName,
        category: formData?.category,
        description: formData?.description,
        price_range: formData?.priceRange,
        image_url: formData?.imageUrl,
        status: formData?.status,
        seo_title: formData?.seoTitle,
        seo_description: formData?.seoDescription,
        technical_specs: formData?.technicalSpecs,
        updated_at: new Date()?.toISOString(),
      };

      if (product?.id) {
        // Update existing product
        const { error: updateError } = await supabase?.from('provider_products')?.update(productData)?.eq('id', product?.id)?.eq('provider_id', user?.id);

        if (updateError) {
          if (updateError?.code?.substring(0, 2) === '42') {
            console.error('Schema error:', updateError?.message);
            throw updateError;
          } else {
            console.log('Update error:', updateError?.message);
            alert('Fout bij bijwerken: ' + updateError?.message);
            return;
          }
        }
      } else {
        // Create new product
        const { error: insertError } = await supabase?.from('provider_products')?.insert([productData]);

        if (insertError) {
          if (insertError?.code?.substring(0, 2) === '42') {
            console.error('Schema error:', insertError?.message);
            throw insertError;
          } else {
            console.log('Insert error:', insertError?.message);
            alert('Fout bij aanmaken: ' + insertError?.message);
            return;
          }
        }
      }

      onClose();
    } catch (err) {
      console.error('Error saving product:', err);
      alert('Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'zonnepanelen', label: 'Zonnepanelen' },
    { value: 'isolatie', label: 'Isolatie' },
    { value: 'warmtepomp', label: 'Warmtepomp' },
    { value: 'ventilatie', label: 'Ventilatie' },
    { value: 'thuisbatterij', label: 'Thuisbatterij' },
    { value: 'waterontharder', label: 'Waterontharder' },
    { value: 'overig', label: 'Overig' },
  ];

  const statusOptions = [
    { value: 'draft', label: 'Concept' },
    { value: 'active', label: 'Actief' },
    { value: 'archived', label: 'Gearchiveerd' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-xl font-heading font-bold text-foreground">
            {product ? 'Product Bewerken' : 'Nieuw Product'}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Basisinformatie</h3>
            
            <Input
              label="Productnaam"
              required
              value={formData?.productName}
              onChange={(e) => setFormData({ ...formData, productName: e?.target?.value })}
              placeholder="Bijv. Premium Zonnepanelen Pakket"
            />

            <Select
              label="Categorie"
              required
              value={formData?.category}
              onChange={(e) => setFormData({ ...formData, category: e?.target?.value })}
            >
              {categories?.map((cat) => (
                <option key={cat?.value} value={cat?.value}>
                  {cat?.label}
                </option>
              ))}
            </Select>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Beschrijving
              </label>
              <textarea
                required
                value={formData?.description}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                rows={4}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Geef een gedetailleerde beschrijving van het product"
              />
            </div>

            <Input
              label="Prijsrange"
              value={formData?.priceRange}
              onChange={(e) => setFormData({ ...formData, priceRange: e?.target?.value })}
              placeholder="Bijv. €6.000 - €12.000"
            />

            <Input
              label="Afbeelding URL"
              value={formData?.imageUrl}
              onChange={(e) => setFormData({ ...formData, imageUrl: e?.target?.value })}
              placeholder="https://example.com/image.jpg"
            />

            <Select
              label="Status"
              value={formData?.status}
              onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
            >
              {statusOptions?.map((opt) => (
                <option key={opt?.value} value={opt?.value}>
                  {opt?.label}
                </option>
              ))}
            </Select>
          </div>

          {/* Technical Specifications */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Technische Specificaties</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Specificatie 1"
                value={formData?.technicalSpecs?.spec1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    technicalSpecs: { ...formData?.technicalSpecs, spec1: e?.target?.value },
                  })
                }
                placeholder="Bijv. Vermogen: 400W"
              />
              <Input
                label="Specificatie 2"
                value={formData?.technicalSpecs?.spec2}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    technicalSpecs: { ...formData?.technicalSpecs, spec2: e?.target?.value },
                  })
                }
                placeholder="Bijv. Efficiency: 21.5%"
              />
              <Input
                label="Specificatie 3"
                value={formData?.technicalSpecs?.spec3}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    technicalSpecs: { ...formData?.technicalSpecs, spec3: e?.target?.value },
                  })
                }
                placeholder="Bijv. Garantie: 25 jaar"
              />
              <Input
                label="Specificatie 4"
                value={formData?.technicalSpecs?.spec4}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    technicalSpecs: { ...formData?.technicalSpecs, spec4: e?.target?.value },
                  })
                }
                placeholder="Bijv. Afmetingen: 1722x1134mm"
              />
            </div>
          </div>

          {/* SEO */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">SEO Optimalisatie</h3>
            <Input
              label="SEO Titel"
              value={formData?.seoTitle}
              onChange={(e) => setFormData({ ...formData, seoTitle: e?.target?.value })}
              placeholder="Optimale titel voor zoekmachines"
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                SEO Beschrijving
              </label>
              <textarea
                value={formData?.seoDescription}
                onChange={(e) => setFormData({ ...formData, seoDescription: e?.target?.value })}
                rows={3}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Korte beschrijving voor zoekmachines (max 160 karakters)"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuleren
            </Button>
            <Button type="submit" loading={loading}>
              {product ? 'Bijwerken' : 'Aanmaken'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;